import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { PutAccountsInfo, GetCompanyInfo } from '../../../services/apiTypes';
import ConfirmModal from '../../../_common/Modal/ConfirmModal';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../../stores/store';
import { HEADER_HEIGHT } from '../../../_constants/styleConst';
import { MAX_COUNT_PARTNER_COMPANY } from '../../../_constants/Const';
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';
import { ArrowForwardIosSharp } from '@mui/icons-material';
import { isAdmin, isAreaManager, isStringInput } from '../../../_utils/utils';
import OrganizationImportExport from './OrganizationImportExport';
import { MaxLength } from '../../../_constants/maxLengthConst';

const styleButton = {
  width: '120px',
  mt: 1,
};

const defaultOrganization: PutAccountsInfo = {
  key: '',
  name: '',
  nameJp: '',
  name03: '',
  name04: '',
  name05: '',
  name06: '',
  name07: '',
  name08: '',
  name09: '',
  name10: '',
  selectedMarketCode: 0,
  shortName: '',
  primaryAgency: '',
  isSkipQC: false,
  partnerCompanyKey: [],
};

type OrganizationEditProps = {
  errorMsg: string;
  updateErrorMsg: (data: string) => void;
  targetOrganizationKey?: string;
  focusOrganization: (key: string) => void;
  handleOrganizationAction: (
    action: 'update' | 'delete',
    targetOrganization: PutAccountsInfo,
  ) => void;
};

const OrganizationUpdate: React.FC<OrganizationEditProps> = ({
  errorMsg,
  updateErrorMsg,
  targetOrganizationKey,
  handleOrganizationAction,
  focusOrganization,
}) => {
  const { t } = useTranslation();
  const { userData } = useSelector((state: RootState) => state.userInfo);
  const { accountsInfoData } = useSelector(
    (state: RootState) => state.accountsInfo,
  );

  /** 組織の変更情報を取る */
  const [targetOrganization, setTargetOrganization] =
    useState<PutAccountsInfo>(defaultOrganization);

  /** パートナーカンパニー管理 */
  const defaultCount =
    targetOrganization?.partnerCompanyKey?.filter((item) => item !== '')
      .length ?? 1;
  const [countPartnerCompany, setCountPartnerCompany] = useState(defaultCount);
  const handleIncrease = () => {
    if (countPartnerCompany < MAX_COUNT_PARTNER_COMPANY) {
      setCountPartnerCompany(countPartnerCompany + 1);
    }
  };
  const handleDecrease = () => {
    if (countPartnerCompany > 0) {
      const updatedCount = countPartnerCompany - 1;
      setCountPartnerCompany(updatedCount);
      setTargetOrganization((prev) => ({
        ...prev,
        partnerCompanyKey: prev?.partnerCompanyKey?.map((key, index) =>
          index === updatedCount ? '' : key,
        ),
      }));
    }
  };

  const convertToPutPostCompanyInfo = (
    data: GetCompanyInfo,
  ): PutAccountsInfo => {
    return {
      key: data.key,
      name: data.name,
      nameJp: data.nameJp,
      name03: data.name03,
      name04: data.name04,
      name05: data.name05,
      name06: data.name06,
      name07: data.name07,
      name08: data.name08,
      name09: data.name09,
      name10: data.name10,
      selectedMarketCode: data.selectedMarketCode,
      shortName: data.shortName,
      isSkipQC: data.isSkipQC,
      primaryAgency: data.primaryAgency.companyKey,
      partnerCompanyKey: data.partnerCompanyList.map((item) => item.companyKey),
    };
  };

  const convertAndSetTargetOrganization = (data: GetCompanyInfo) => {
    setTargetOrganization(convertToPutPostCompanyInfo(data));
  };

  /** 全てフィールドを確認 */
  const [isDisabledSaveBtn, setIsDisabledSaveBtn] = useState(true);
  useEffect(() => {
    const requiredFields = ['key', 'name', 'primaryAgency'];
    const isAllFilled = requiredFields.every((item) => {
      const key = item as keyof PutAccountsInfo;
      return isStringInput(targetOrganization[key]);
    });
    setIsDisabledSaveBtn(!isAllFilled);
  }, [targetOrganization]);

  /** クリア押下時 */
  const rollBackData = () => {
    const data = accountsInfoData.companyList.find(
      (item) => item.key === targetOrganizationKey,
    );
    if (data) {
      convertAndSetTargetOrganization(data);
      setCountPartnerCompany(
        convertToPutPostCompanyInfo(data)?.partnerCompanyKey?.length ?? 0,
      );
    } else {
      setTargetOrganization(defaultOrganization);
      setCountPartnerCompany(1);
    }
  };

  /** 組織のパス */
  const [targetOrganizationPath, setTargetOrganizationPath] = useState<
    string[]
  >([]);
  useEffect(() => {
    rollBackData();
    if (targetOrganizationKey) {
      const arrayPath = findAllParents(
        accountsInfoData.companyList,
        targetOrganizationKey,
      );
      setTargetOrganizationPath(arrayPath.reverse());
    }
  }, [accountsInfoData.companyList, targetOrganizationKey]);

  /** 組織のパスでクリックイベントを適用　*/
  const ClickTextPath = () => {
    return (
      <Box display="flex" alignItems="flex-end" minHeight="43px">
        <Typography>
          {targetOrganizationPath.map((part, index) => {
            const data = accountsInfoData.companyList.find(
              (item) => item.key === part,
            );
            return (
              <Box component="span" key={index}>
                <Box
                  component="span"
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      color: 'primary.dark',
                      textDecoration: 'underline',
                    },
                  }}
                  onClick={() => {
                    if (data) {
                      focusOrganization(data.key);
                    }
                  }}
                >
                  {data?.name ?? part}
                </Box>
                {index < targetOrganizationPath.length - 1 && (
                  <Box component="span">
                    <ArrowForwardIosSharp
                      sx={{
                        verticalAlign: 'middle',
                        color: 'primary.main',
                        fontSize: 'medium',
                        mx: 0.5,
                      }}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        </Typography>
      </Box>
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateErrorMsg('');
    const { name, value } = e.target;
    if (name.includes('partnerCompany')) {
      const partnerIndex = Number(name.charAt(name.length - 1));
      const newPartnerCompanyKey = [
        ...(targetOrganization.partnerCompanyKey ??
          Array(MAX_COUNT_PARTNER_COMPANY).fill('')),
      ];
      newPartnerCompanyKey[partnerIndex] = value;
      setTargetOrganization((prev) => ({
        ...prev,
        partnerCompanyKey: newPartnerCompanyKey,
      }));
      return;
    }
    setTargetOrganization((prev) => ({
      ...prev,
      [name]:
        typeof prev[name as keyof PutAccountsInfo] === 'number'
          ? Number(value)
          : value,
    }));
  };

  /** 出荷検査スキップフラグの変更 */
  const handleIsSkipQc = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.checked;
    setTargetOrganization((prev) => ({
      ...prev,
      isSkipQC: value,
    }));
  };

  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);

  /** 上位組織をすべて取得 */
  const findAllParents = (
    data: GetCompanyInfo[],
    targetKey: string,
  ): string[] => {
    const path: string[] = [];
    const nodeMap = new Map<string, GetCompanyInfo>();
    data.forEach((item) => nodeMap.set(item.key, item));

    let currentKey: string | undefined = targetKey;
    while (currentKey) {
      const currentNode = nodeMap.get(currentKey);
      if (!currentNode) break;
      path.push(currentNode.key);
      if (
        !currentNode.primaryAgency ||
        currentNode.primaryAgency.companyKey === currentNode.key
      )
        break;
      currentKey = currentNode.primaryAgency.companyKey;
    }
    return path;
  };

  /** 下位組織以外をすべて取得 */
  const findAllNotChildren = (
    data: GetCompanyInfo[],
    targetKey: string | undefined,
  ): GetCompanyInfo[] => {
    if (!targetKey) return [];
    const childrenKeys = new Set<string>();

    const findChildrenKeys = (parentKey: string) => {
      const directChildren = data.filter(
        (node) =>
          node.primaryAgency.companyKey === parentKey &&
          node.primaryAgency.companyKey !== node.key,
      );
      for (const child of directChildren) {
        childrenKeys.add(child.key);
        findChildrenKeys(child.key);
      }
    };
    findChildrenKeys(targetKey);

    return data.filter((node) => !childrenKeys.has(node.key));
  };

  return (
    <Box sx={{ mb: 2 }}>
      {ClickTextPath()}
      {errorMsg && (
        <Typography color="error" sx={{ mb: 1 }}>
          {errorMsg}
        </Typography>
      )}
      <Box
        sx={{
          overflowY: 'auto',
          maxHeight: `calc(100dvh - ${HEADER_HEIGHT} - 7rem)`,
        }}
      >
        {/* ID */}
        <TextField
          autoFocus
          margin="dense"
          label={t('LBL_ID')}
          name="key"
          fullWidth
          value={targetOrganization.key}
          disabled={targetOrganization.key === '' || isAdmin(userData)}
          slotProps={{
            input: { readOnly: isAreaManager(userData) },
          }}
        />
        {/* 名前 */}
        <TextField
          margin="dense"
          label={t('LBL_NAME')}
          name="name"
          fullWidth
          value={targetOrganization.name}
          onChange={handleInputChange}
          disabled={targetOrganization.key === ''}
          slotProps={{
            input: { readOnly: isAreaManager(userData) },
            htmlInput: { maxLength: MaxLength.ManageOrganization.NAME },
          }}
        />
        {/* 略称 */}
        <TextField
          margin="dense"
          label={t('LBL_SHORT_NAME')}
          name="shortName"
          fullWidth
          value={targetOrganization.shortName}
          onChange={handleInputChange}
          disabled={targetOrganization.key === ''}
          slotProps={{
            input: { readOnly: isAreaManager(userData) },
            htmlInput: { maxLength: MaxLength.ManageOrganization.SHORT_NAME },
          }}
        />

        {/* マーケット */}
        {isAdmin(userData) ? (
          <TextField
            margin="dense"
            label={t('LBL_MARKET')}
            name="selectedMarketCode"
            select
            fullWidth
            value={targetOrganization.selectedMarketCode}
            onChange={handleInputChange}
            disabled={targetOrganization.key === ''}
            slotProps={{
              inputLabel: { shrink: targetOrganization.key !== '' },
            }}
          >
            {accountsInfoData.marketCodeList.map((item, i) => (
              <MenuItem
                key={`market_code_${i}`}
                value={item.code}
                sx={{ display: item.code === 0 ? 'none' : 'block' }}
              >
                {item.name}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <TextField
            margin="dense"
            label={t('LBL_MARKET')}
            fullWidth
            value={
              accountsInfoData.marketCodeList.find(
                (e) => e.code === targetOrganization.selectedMarketCode,
              )?.name ?? ''
            }
            disabled={targetOrganization.key === ''}
            slotProps={{
              input: { readOnly: true },
              inputLabel: { shrink: targetOrganization.key !== '' },
            }}
            sx={{
              '& .MuiSelect-icon': { display: 'none' },
            }}
          />
        )}

        {/* 上位組織 */}
        {isAreaManager(userData) ? (
          <TextField
            margin="dense"
            label={t('LBL_PARENT_ORGANIZATION')}
            name="primaryAgency"
            fullWidth
            value={
              accountsInfoData.companyList.find(
                (item) => item.key === targetOrganization.key,
              )?.primaryAgency.companyName ?? ''
            }
            disabled={targetOrganization.key === ''}
            slotProps={{
              input: { readOnly: true },
            }}
          />
        ) : (
          <Autocomplete
            options={findAllNotChildren(
              accountsInfoData.companyList,
              targetOrganizationKey,
            )}
            noOptionsText={t('MSG_NO_OPTION')}
            disableClearable={targetOrganization.primaryAgency === ''}
            getOptionLabel={(option) => option.name ?? ''}
            isOptionEqualToValue={(option, value) => option.key === value.key}
            disabled={targetOrganization.key === ''}
            value={
              accountsInfoData.companyList.find(
                (item) => item.key === targetOrganization.primaryAgency,
              ) || null
            }
            onChange={(_, newValue) => {
              handleInputChange({
                target: {
                  name: 'primaryAgency',
                  value: newValue?.key ?? '',
                },
              } as React.ChangeEvent<HTMLInputElement>);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('LBL_PARENT_ORGANIZATION')}
                margin="dense"
                fullWidth
              />
            )}
          />
        )}

        {targetOrganization.key !== '' && (
          <Box>
            {/** 「No fault found」選択時に出荷検査をスキップするかどうか */}
            <Box width="100%" display="flex">
              <Checkbox
                checked={targetOrganization.isSkipQC}
                onChange={(e) => handleIsSkipQc(e)}
                disabled={!isAdmin(userData)}
              />
              <Typography sx={{ alignSelf: 'center', pl: 1 }}>
                {t('LBL_SKIP_QC')}
              </Typography>
            </Box>
            {/* ShopUse */}
            <OrganizationImportExport
              companyKey={targetOrganization.key}
              companyName={targetOrganization.name}
            />
            {/*  パートナーカンパニー */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                my: '0.3rem',
              }}
            >
              <IconButton
                onClick={handleDecrease}
                disabled={countPartnerCompany <= 0}
                color="primary"
                sx={{
                  display: isAdmin(userData) ? '' : 'none',
                }}
              >
                <RemoveCircleOutline />
              </IconButton>
              <Typography sx={{ minWidth: 120 }}>
                {`${t('LBL_PARTNER_COMPANY')} : ${countPartnerCompany} / ${MAX_COUNT_PARTNER_COMPANY}`}
              </Typography>
              <IconButton
                onClick={handleIncrease}
                disabled={countPartnerCompany >= MAX_COUNT_PARTNER_COMPANY}
                color="primary"
                sx={{
                  display: isAdmin(userData) ? '' : 'none',
                }}
              >
                <AddCircleOutline />
              </IconButton>
            </Box>
            {/* パートナーカンパニー一覧 */}
            {Array(countPartnerCompany)
              .fill('')
              .map((_, i) => {
                const selectedKey =
                  targetOrganization.partnerCompanyKey?.[i] ?? '';
                return isAreaManager(userData) ? (
                  <TextField
                    margin="dense"
                    label={t('LBL_PARTNER_COMPANY_NO', { 0: i + 1 })}
                    name={`partnerCompany${i}`}
                    fullWidth
                    value={
                      accountsInfoData.companyList.find(
                        (item) => item.key === targetOrganization.key,
                      )?.partnerCompanyList[i]?.companyName ?? ''
                    }
                    slotProps={{
                      input: { readOnly: true },
                    }}
                  />
                ) : (
                  <Autocomplete
                    key={`partner_company_${i}`}
                    options={accountsInfoData.companyList.filter(
                      (item) => item.key !== targetOrganization.key,
                    )}
                    noOptionsText={t('MSG_NO_OPTION')}
                    disableClearable={selectedKey === ''}
                    getOptionLabel={(option) => option.name ?? ''}
                    isOptionEqualToValue={(option, value) =>
                      option.key === value.key
                    }
                    getOptionDisabled={(option) =>
                      option.key !== selectedKey &&
                      !!targetOrganization.partnerCompanyKey?.includes(
                        option.key,
                      )
                    }
                    value={
                      accountsInfoData.companyList.find(
                        (item) => item.key === selectedKey,
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      handleInputChange({
                        target: {
                          name: `partnerCompany${i}`,
                          value: newValue?.key ?? '',
                        },
                      } as React.ChangeEvent<HTMLInputElement>);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        margin="dense"
                        label={t('LBL_PARTNER_COMPANY_NO', { 0: i + 1 })}
                        fullWidth
                      />
                    )}
                  />
                );
              })}
          </Box>
        )}
      </Box>
      {/* ボタン */}
      <Box
        sx={{
          display: isAdmin(userData) ? 'flex' : 'none',
          justifyContent: 'space-between',
        }}
      >
        <Button
          sx={styleButton}
          color="error"
          variant="contained"
          onClick={() => setIsOpenDeleteModal(true)}
          disabled={targetOrganization.key === ''}
        >
          {t('BTN_DELETE')}
        </Button>
        <Box sx={{ display: 'flex' }}>
          <Button
            color="inherit"
            sx={{ ...styleButton, mr: { xs: 1, md: 3 }, ml: 1 }}
            variant="contained"
            onClick={rollBackData}
            disabled={targetOrganization.key === ''}
          >
            {t('BTN_CLEAR')}
          </Button>
          <Button
            color="primary"
            variant="contained"
            sx={styleButton}
            onClick={() => {
              updateErrorMsg('');
              handleOrganizationAction('update', targetOrganization);
            }}
            disabled={isDisabledSaveBtn}
          >
            {t('BTN_SAVE')}
          </Button>
        </Box>
      </Box>
      <ConfirmModal
        isOpen={isOpenDeleteModal}
        displayMessage={t('MSG_DELETE_ORGANIZATION')}
        onClickOk={() => {
          setIsOpenDeleteModal(false);
          handleOrganizationAction('delete', targetOrganization);
        }}
        onClickCancel={() => setIsOpenDeleteModal(false)}
      />
    </Box>
  );
};

export default OrganizationUpdate;
